/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {
  Device
} from '@ion/core/src/types/entities';
import {
  ServiceError
} from '@ion/core/src/types/services';
import {titleCase} from '@ion/core/src/utils/StringUtil';
import {IonSnackbar} from '../notifications/IonSnackbar';
import DeletePrompt from './DeletePrompt';

// defines the DeviceDeletePrompt Prop type definitions ...
export interface DeviceDeletePromptProps {
  show: boolean;
  device: Device | null;
  entity?: string;
  loading?: boolean;
  overrideMessage?: Function | null;
  onDeleteConfirm: (device: Device) => void;
  onDeleteCancel: () => void;
  onClose?: () => void;
  showError?: boolean;
  showSuccess?: boolean;
};

/**
 * The DeviceDeletePrompt provides the UI for prompting, confirming, or canceling a Device
 * Delete action.
 *
 * ```typescript
 *
 *    import {DeviceDeletePrompt} from '@ion/dls/src/editors/';
 *
 *    const {
 *      selected,
 *      result,
 *      isEditing,
 *      loading,
 *      error
 *    } = useSelector(({devices: {destroy}}) => destroy);
 *
 *    const onDeleteConfirm = function(device: Device) {
 *      dispatch(deleteDevice(selected));
 *    };
 *
 *    function onDeleteCancel() {
 *      dispatch(deleteDevice(selected, false));
 *    }
 *
 *    return (
 *      <DeviceDeletePrompt show={isEditing}
 *        device={selected}
 *        onDeleteConfirm={onDeleteConfirm}
 *        onDeleteCancel={onDeleteCancel}
 *        result={result}
 *        loading={loading}
 *        error={error} />
 *    );
 *
 * ```
 */
export const DeviceDeletePrompt = ({
  show,
  device,
  entity,
  loading,
  onDeleteConfirm,
  onDeleteCancel,
  onClose,
  showError = false,
  showSuccess = false,
  overrideMessage = null
}: DeviceDeletePromptProps) => {
  if (show || showError || showSuccess) {
    return (
      <>
        {show && (
          <DeletePrompt show
            entity={entity}
            items={device}
            isLoading={loading}
            overrideMessage={overrideMessage}
            onConfirm={function() {
              onDeleteConfirm(device);
            }}
            onCancel={function() {
              onDeleteCancel();
            }} />
        )}
        {showError && (
          <IonSnackbar show
            message={translate('common:editors.delete.failure', {entity})}
            actionLabel={translate('notifications:warning.actionLabel')}
            onAction={onClose}
            onClose={onClose}
            autoHideDuration={3000} />
        )}
        {showSuccess && (
          <IonSnackbar show
            message={translate('common:editors.delete.success', {entity: titleCase(entity)})}
            actionLabel={translate('common:editors.delete.ok')}
            onAction={onClose}
            onClose={onClose}
            autoHideDuration={3000} />
        )}
      </>
    );
  };
  return null;
};

export default DeviceDeletePrompt;
